import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Group,
  Text,
  Stack,
  TextInput,
  Switch,
  SegmentedControl,
  Select,
  NumberInput,
  Divider,
  useMantineTheme,
  ColorInput,
  Collapse,
  useComputedColorScheme,
} from '@mantine/core';
import { IconSettings, IconResize, IconRefresh, IconPalette, IconDroplet } from '@tabler/icons-react';
import { Modal, Button } from '../../../ui';
import { useDashboardStore, selectSettingsModalWidget } from '../../../../stores/dashboardStore';
import { getWidget } from '../registry';
import {
  WidgetSettings,
  WidgetSizeVariant,
  StatWidgetSizeVariant,
  ListWidgetSizeVariant,
  ChartWidgetSizeVariant,
} from '../types';
import { formatSizeDisplay, getSizeDescription, getWidgetSizeForBreakpoint } from '../layout/breakpoints';
import { pickTextColorForBackground } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// WidgetSettingsModal Component
// ============================================================================

export interface WidgetSettingsModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export function WidgetSettingsModal({ opened, onClose }: WidgetSettingsModalProps) {
  const theme = useMantineTheme();
  const settingsModalWidget = useDashboardStore(selectSettingsModalWidget);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };
  const widgets = useDashboardStore((state) => state.widgets);
  const setWidgetSettings = useDashboardStore((state) => state.setWidgetSettings);
  const closeSettingsModal = useDashboardStore((state) => state.closeSettingsModal);

  // Get the widget instance and definition
  const widgetInstance = useMemo(() => {
    if (!settingsModalWidget) return null;
    return widgets.find((w) => w.instanceId === settingsModalWidget);
  }, [settingsModalWidget, widgets]);

  const widgetDefinition = useMemo(() => {
    if (!widgetInstance) return null;
    return getWidget(widgetInstance.widgetId);
  }, [widgetInstance]);

  // Local state for form
  const [localSettings, setLocalSettings] = useState<WidgetSettings>({});

  // Initialize local state when modal opens
  useEffect(() => {
    if (widgetInstance) {
      setLocalSettings(widgetInstance.settings || {});
    }
  }, [widgetInstance]);

  // Handle save
  const handleSave = () => {
    if (settingsModalWidget) {
      setWidgetSettings(settingsModalWidget, localSettings);
    }
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    closeSettingsModal();
    onClose();
  };

  // Update local setting
  const updateSetting = <K extends keyof WidgetSettings>(key: K, value: WidgetSettings[K]) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Get size variant options based on widget category
  const sizeVariantOptions = useMemo(() => {
    if (!widgetDefinition?.sizeVariants) return [];

    return widgetDefinition.sizeVariants.variants
      .map((variant) => {
        const sizes = widgetDefinition.sizeVariants!.variantSizes[variant];
        // Skip variants that don't have sizes defined
        if (!sizes) return null;

        const baseSize = getWidgetSizeForBreakpoint(sizes, 'lg');
        const sizeText = formatSizeDisplay(baseSize);
        const description = getSizeDescription(baseSize);

        return {
          value: variant,
          label: `${variant.charAt(0).toUpperCase() + variant.slice(1)} (${sizeText})`,
          description,
        };
      })
      .filter((opt): opt is NonNullable<typeof opt> => opt !== null);
  }, [widgetDefinition]);

  if (!widgetInstance || !widgetDefinition) {
    return null;
  }

  const Icon = widgetDefinition.icon;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconSettings size={20} />
          <Text fw={600} style={accessibilityStyles}>Widget Settings</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {/* Widget Info */}
        <Box
          p="sm"
          style={{
            backgroundColor: theme.colors.gray[0],
            borderRadius: theme.radius.md,
          }}
        >
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: theme.radius.md,
                backgroundColor: theme.colors.blue[1],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={20} />
            </Box>
            <Box>
              <Text fw={600} size="sm" style={accessibilityStyles}>
                {widgetDefinition.name}
              </Text>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {widgetDefinition.description}
              </Text>
            </Box>
          </Group>
        </Box>

        <Divider />

        {/* Custom Title */}
        <TextInput
          label="Custom Title"
          description="Override the default widget title"
          placeholder={widgetDefinition.name}
          value={localSettings.customTitle || ''}
          onChange={(e) => updateSetting('customTitle', e.currentTarget.value || undefined)}
        />

        {/* Size Variant Selection */}
        {widgetDefinition.sizeVariants && sizeVariantOptions.length > 0 && (
          <Box>
            <Group gap="xs" mb="xs">
              <IconResize size={16} />
              <Text size="sm" fw={500} style={accessibilityStyles}>
                Size Variant
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mb="sm" style={accessibilityStyles}>
              Configure widget size for different screen sizes. Larger variants improve readability on smaller screens.
            </Text>
            <Stack gap="sm">
              <Select
                label="Desktop"
                description="Large screens (992px+) - full flexibility"
                placeholder="Select size"
                value={localSettings.sizeVariant || widgetDefinition.sizeVariants.defaultVariant}
                onChange={(value) => updateSetting('sizeVariant', value as WidgetSizeVariant)}
                data={sizeVariantOptions}
              />
              <Select
                label="Tablet / iPad"
                description="Medium screens (768-992px) - recommend 4+ columns"
                placeholder="Same as desktop"
                value={localSettings.tabletSizeVariant || ''}
                onChange={(value) => updateSetting('tabletSizeVariant', value ? (value as WidgetSizeVariant) : undefined)}
                data={[
                  { value: '', label: 'Same as desktop' },
                  ...sizeVariantOptions,
                ]}
                clearable
              />
              <Select
                label="Phone"
                description="Small screens (<768px) - recommend 6 or 12 columns"
                placeholder="Same as tablet"
                value={localSettings.phoneSizeVariant || ''}
                onChange={(value) => updateSetting('phoneSizeVariant', value ? (value as WidgetSizeVariant) : undefined)}
                data={[
                  { value: '', label: 'Same as tablet' },
                  ...sizeVariantOptions,
                ]}
                clearable
              />
            </Stack>
            {(localSettings.sizeVariant || localSettings.tabletSizeVariant || localSettings.phoneSizeVariant) && (
              <Text size="xs" c="dimmed" mt="xs" style={accessibilityStyles}>
                Desktop: {sizeVariantOptions.find((o) => o.value === (localSettings.sizeVariant || widgetDefinition.sizeVariants?.defaultVariant))?.description || 'Default'}
                {localSettings.tabletSizeVariant && (
                  <> | Tablet: {sizeVariantOptions.find((o) => o.value === localSettings.tabletSizeVariant)?.description}</>
                )}
                {localSettings.phoneSizeVariant && (
                  <> | Phone: {sizeVariantOptions.find((o) => o.value === localSettings.phoneSizeVariant)?.description}</>
                )}
              </Text>
            )}
          </Box>
        )}

        {/* Auto Refresh */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconRefresh size={16} />
            <Text size="sm" fw={500} style={accessibilityStyles}>
              Auto Refresh
            </Text>
          </Group>
          <Switch
            label="Enable auto-refresh"
            checked={localSettings.autoRefresh || false}
            onChange={(e) => updateSetting('autoRefresh', e.currentTarget.checked)}
            mb="xs"
          />
          {localSettings.autoRefresh && (
            <NumberInput
              label="Refresh interval (seconds)"
              value={localSettings.refreshInterval || 60}
              onChange={(value) => updateSetting('refreshInterval', typeof value === 'number' ? value : 60)}
              min={10}
              max={3600}
              step={10}
            />
          )}
        </Box>

        {/* Theme Variant */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconPalette size={16} />
            <Text size="sm" fw={500} style={accessibilityStyles}>
              Theme
            </Text>
          </Group>
          <SegmentedControl
            value={localSettings.themeVariant || 'default'}
            onChange={(value) => updateSetting('themeVariant', value as WidgetSettings['themeVariant'])}
            data={[
              { value: 'default', label: 'Default' },
              { value: 'gradient', label: 'Gradient' },
              { value: 'minimal', label: 'Minimal' },
              { value: 'custom', label: 'Custom' },
            ]}
            fullWidth
          />
        </Box>

        {/* Custom Theme Options - Only show when 'custom' is selected */}
        <Collapse in={localSettings.themeVariant === 'custom'}>
          <Stack gap="sm" pt="xs">
            <Box>
              <Group gap="xs" mb="xs">
                <IconDroplet size={16} />
                <Text size="sm" fw={500} style={accessibilityStyles}>
                  Custom Colors
                </Text>
              </Group>
              <ColorInput
                label="Background Color"
                description="Set a custom background color for this widget"
                placeholder="#ffffff"
                value={localSettings.backgroundColor || ''}
                onChange={(color) => updateSetting('backgroundColor', color || undefined)}
                format="hex"
                swatches={[
                  '#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb',
                  '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886',
                  '#40c057', '#82c91e', '#fab005', '#fd7e14',
                ]}
              />
            </Box>

            <ColorInput
              label="Custom Gradient (Optional)"
              description="Use a second color to create a gradient background"
              placeholder="Leave empty for solid color"
              value={localSettings.backgroundGradient || ''}
              onChange={(color) => updateSetting('backgroundGradient', color || undefined)}
              format="hex"
              swatches={[
                '#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb',
                '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886',
                '#40c057', '#82c91e', '#fab005', '#fd7e14',
              ]}
            />

            <Switch
              label="Auto text color"
              description="Automatically pick the best text color for readability based on background"
              checked={localSettings.textColor === 'auto'}
              onChange={(e) =>
                updateSetting('textColor', e.currentTarget.checked ? 'auto' : undefined)
              }
            />

            {localSettings.backgroundColor && (
              <Box
                p="md"
                style={{
                  borderRadius: theme.radius.md,
                  background: localSettings.backgroundGradient
                    ? `linear-gradient(135deg, ${localSettings.backgroundColor}, ${localSettings.backgroundGradient})`
                    : localSettings.backgroundColor,
                  color:
                    localSettings.textColor === 'auto'
                      ? pickTextColorForBackground(localSettings.backgroundColor)
                      : undefined,
                }}
              >
                <Text size="sm" fw={500} style={accessibilityStyles}>
                  Preview
                </Text>
                <Text size="xs" style={accessibilityStyles}>This is how your widget will look</Text>
              </Box>
            )}
          </Stack>
        </Collapse>

        {/* Show Header */}
        <Switch
          label="Show header"
          description="Hide header for a cleaner look"
          checked={localSettings.showHeader !== false}
          onChange={(e) => updateSetting('showHeader', e.currentTarget.checked)}
        />

        <Divider />

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Settings</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default WidgetSettingsModal;
